import React from 'react';

// Injects GA tracking code / script tag
import 'common/ui/GoogleAnalytics';
// Sets up hash change listeners for logging page views
import 'client/app/lib/GoogleAnalyticsLogger';

import { StyledEngineProvider } from '@mui/material/styles';
import createGenerateClassName from '@mui/styles/createGenerateClassName';
import StylesProvider from '@mui/styles/StylesProvider';
import { ErrorBoundary } from '@sentry/react';
import { enableAllPlugins as enableAllImmerPlugins } from 'immer';
import { createRoot } from 'react-dom/client';
import { Route, Switch } from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter5Adapter } from 'use-query-params/adapters/react-router-5';

import LoginCheck from 'client/app/apps/authentication/components/LoginCheck';
import { ConnectivityTest } from 'client/app/apps/connectivity-test/ConnectivityTest';
import PlatformLayoutSimplified from 'client/app/apps/index/PlatformLayoutSimplified';
import ToggleRouter from 'client/app/apps/index/ToggleRouter';
import { AnthaHubLogin } from 'client/app/apps/Login/AnthaHubLogin';
import errorFallback from 'client/app/components/ErrorFallback';
import { PermissionsProvider } from 'client/app/components/Permissions';
import { startErrorHandler } from 'client/app/lib/errors';
import { APP_VERSION, COMMIT_SHA } from 'client/app/version';
import { FeatureTogglesContextProvider } from 'common/features/FeatureTogglesContext';
import GraphQLPlayground from 'common/ui/GraphQLPlayground';
import { setFaviconForEnvironment } from 'common/ui/lib/envs';
import makeGlobalStylesHook from 'common/ui/makeGlobalStylesHook';
import { LightTheme } from 'common/ui/theme';

enableAllImmerPlugins();

startErrorHandler({
  service: 'antha-platform',
  version: APP_VERSION,
  commitSHA: COMMIT_SHA,
  location: window.location,
});

// To avoid class names clashes, customise the behaviour
// for auto-generated class names
const generateClassName = createGenerateClassName({
  productionPrefix: 'antha-',
});

const useGlobalStyles = makeGlobalStylesHook();

function AnthaApp() {
  useGlobalStyles();

  return (
    <StyledEngineProvider injectFirst>
      <LightTheme>
        <StylesProvider generateClassName={generateClassName} injectFirst>
          <LoginCheck>
            <FeatureTogglesContextProvider>
              <ErrorBoundary fallback={errorFallback}>
                <PermissionsProvider>
                  <ToggleRouter>
                    <QueryParamProvider adapter={ReactRouter5Adapter}>
                      <Switch>
                        <Route exact path="/anthahub-login">
                          <AnthaHubLogin />
                        </Route>
                        <Route exact path="/graphql/internal">
                          <GraphQLPlayground url="/web/graphql" />
                        </Route>
                        <Route>
                          <PlatformLayoutSimplified />
                        </Route>
                      </Switch>
                    </QueryParamProvider>
                  </ToggleRouter>
                </PermissionsProvider>
              </ErrorBoundary>
            </FeatureTogglesContextProvider>
          </LoginCheck>
        </StylesProvider>
      </LightTheme>
    </StyledEngineProvider>
  );
}

setFaviconForEnvironment();

const url = new URL(window.location.href);
const appToRender = url.pathname.startsWith('/connectivity-test') ? (
  <ConnectivityTest />
) : (
  <AnthaApp />
);

const el = document.getElementById('root');
const root = createRoot(el!);
root.render(appToRender);
